import type { Address } from 'viem';

// eslint-disable-next-line deprecate/import
import { type WagmiConfigChain } from '@shared/common/providers/LegacyWeb3Provider/wagmi';
import { handleTransactionResult } from '@shared/helpers/web3/handleTransactionResult';

import { useAllowance } from './useAllowance';
import { useApprove } from './useApprove';

type Props = {
  amount: number | string;
  chainId: WagmiConfigChain['id'];
  spenderAddress: Address;
  tokenAddress: Address;
  tokenDecimals: number;
  /**
   * Optional. Defines the amount to compare with the current allowance.
   * If not provided, it defaults to the value of `amount`.
   * This determines whether an approval transaction is necessary.
   */
  amountToCheck?: number | string;
};

export const useApproveIfNeeded = () => {
  const { checkIsEnoughDepositAllowance } = useAllowance();
  const { approve } = useApprove();

  const approveIfNeeded = async ({
    amount,
    chainId,
    spenderAddress,
    tokenAddress,
    tokenDecimals,
    amountToCheck = amount,
  }: Props) => {
    const isEnoughAllowance = await checkIsEnoughDepositAllowance({
      amount: amountToCheck,
      chainId,
      spenderAddress,
      tokenAddress,
      tokenDecimals,
    });

    if (isEnoughAllowance === undefined) {
      return;
    }

    if (!isEnoughAllowance) {
      const increasingTransaction = await approve({
        amount,
        chainId,
        spenderAddress,
        tokenAddress,
        tokenDecimals,
      });

      if (!increasingTransaction) {
        return;
      }

      // Wait for the transaction to complete before do the next step.
      await handleTransactionResult(increasingTransaction, chainId);

      // Check the allowance again due to it could be changed in the wallet on runtime
      await approveIfNeeded({
        amount,
        chainId,
        spenderAddress,
        tokenAddress,
        tokenDecimals,
      });
    }
  };

  return { approveIfNeeded };
};
